
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface GachaItem {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  min_level: number;
}

interface GachaWheelProps {
  canGacha: boolean;
  userLevel: number;
  onGachaComplete: () => void;
}

const rarityColors = {
  common: 'from-gray-400 to-gray-600',
  rare: 'from-blue-400 to-blue-600',
  epic: 'from-purple-400 to-purple-600',
  legendary: 'from-yellow-400 to-yellow-600',
};

const rarityNames = {
  common: '일반',
  rare: '레어',
  epic: '에픽',
  legendary: '전설',
};

export default function GachaWheel({ canGacha, userLevel, onGachaComplete }: GachaWheelProps) {
  const { user } = useAuth();
  const [items, setItems] = useState<GachaItem[]>([]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<GachaItem | null>(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [userLevel]);

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('gacha_items')
        .select('*')
        .eq('is_active', true)
        .lte('min_level', userLevel);

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('아이템 로딩 오류:', error);
    }
  };

  const getRarityWeight = (rarity: string, level: number) => {
    const baseWeights = {
      common: 50,
      rare: 30,
      epic: 15,
      legendary: 5,
    };

    let weight = baseWeights[rarity as keyof typeof baseWeights] || 0;

    // 레벨에 따른 가중치 조정
    if (level < 3 && rarity !== 'common') weight *= 0.5;
    if (level < 5 && rarity === 'epic') weight = 0;
    if (level < 7 && rarity === 'legendary') weight = 0;
    if (level >= 10) weight *= 2;

    return weight;
  };

  const selectRandomItem = () => {
    const weightedItems: { item: GachaItem; weight: number }[] = items.map(item => ({
      item,
      weight: getRarityWeight(item.rarity, userLevel),
    }));

    const totalWeight = weightedItems.reduce((sum, wi) => sum + wi.weight, 0);
    let random = Math.random() * totalWeight;

    for (const wi of weightedItems) {
      random -= wi.weight;
      if (random <= 0) {
        return wi.item;
      }
    }

    return weightedItems[0]?.item;
  };

  const handleGacha = async () => {
    if (!canGacha || spinning || !user || items.length === 0) return;

    setSpinning(true);
    setShowResult(false);

    // 애니메이션 효과
    setTimeout(async () => {
      const selectedItem = selectRandomItem();
      
      if (!selectedItem) {
        alert('뽑기 가능한 아이템이 없습니다');
        setSpinning(false);
        return;
      }

      try {
        // 뽑기 기록 저장
        const today = new Date().toISOString().split('T')[0];
        
        const { error: dailyError } = await supabase
          .from('user_daily_gacha')
          .insert([{
            user_id: user.id,
            last_gacha_date: today,
          }]);

        if (dailyError) throw dailyError;

        // 아이템 획득 기록
        const { error: historyError } = await supabase
          .from('user_gacha_history')
          .insert([{
            user_id: user.id,
            item_id: selectedItem.id,
          }]);

        if (historyError) throw historyError;

        setResult(selectedItem);
        setShowResult(true);
        onGachaComplete();
      } catch (error) {
        console.error('뽑기 오류:', error);
        alert('뽑기 중 오류가 발생했습니다');
      } finally {
        setSpinning(false);
      }
    }, 2000);
  };

  if (items.length === 0) {
    return (
      <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-xl p-12">
        <div className="text-center">
          <i className="ri-gift-line text-6xl text-white/50 mb-4"></i>
          <p className="text-white/80 text-lg">현재 레벨에서 뽑을 수 있는 아이템이 없습니다</p>
        </div>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-xl p-8">
      {/* 뽑기 버튼 */}
      <div className="text-center mb-8">
        <div className="relative inline-block">
          <button
            onClick={handleGacha}
            disabled={!canGacha || spinning}
            className={`relative w-64 h-64 rounded-full font-extrabold text-2xl transition-all whitespace-nowrap cursor-pointer ${
              canGacha && !spinning
                ? 'bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white hover:scale-105 shadow-2xl'
                : 'bg-white/20 text-white/50 cursor-not-allowed'
            } ${spinning ? 'animate-spin' : ''}`}
          >
            {spinning ? (
              <i className="ri-loader-4-line text-6xl"></i>
            ) : canGacha ? (
              <div>
                <i className="ri-gift-line text-6xl mb-2"></i>
                <div className="text-xl">뽑기 시작</div>
              </div>
            ) : (
              <div>
                <i className="ri-time-line text-6xl mb-2"></i>
                <div className="text-xl">내일 다시</div>
              </div>
            )}
          </button>
        </div>

        {!canGacha && !spinning && (
          <p className="text-white/80 mt-6 font-semibold">
            오늘의 뽑기를 이미 사용했습니다. 내일 다시 도전하세요!
          </p>
        )}
      </div>

      {/* 결과 표시 */}
      {showResult && result && (
        <div className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-xl p-8 mb-8 animate-fade-in">
          <div className="text-center">
            <h3 className="text-2xl font-extrabold text-white mb-4">
              <i className="ri-trophy-line mr-2"></i>
              축하합니다!
            </h3>
            
            <div className={`inline-block bg-gradient-to-r ${rarityColors[result.rarity]} rounded-xl p-6 mb-4`}>
              {result.image_url ? (
                <img 
                  src={result.image_url} 
                  alt={result.name}
                  className="w-32 h-32 object-contain mx-auto"
                />
              ) : (
                <i className="ri-treasure-map-line text-8xl text-white"></i>
              )}
            </div>

            <div className="mb-2">
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-extrabold bg-gradient-to-r ${rarityColors[result.rarity]} text-white`}>
                {rarityNames[result.rarity]}
              </span>
            </div>

            <h4 className="text-2xl font-extrabold text-white mb-2">{result.name}</h4>
            <p className="text-white/80">{result.description}</p>
          </div>
        </div>
      )}

      {/* 확률 정보 */}
      <div className="border-t border-white/20 pt-6">
        <h3 className="text-lg font-extrabold text-white mb-4">등급별 확률</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(rarityNames).map(([key, name]) => {
            const weight = getRarityWeight(key, userLevel);
            const totalWeight = items.reduce((sum, item) => 
              sum + getRarityWeight(item.rarity, userLevel), 0
            );
            const probability = totalWeight > 0 ? (weight / totalWeight * 100).toFixed(1) : '0.0';

            return (
              <div key={key} className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-lg p-4">
                <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-r ${rarityColors[key as keyof typeof rarityColors]} rounded-lg mb-2 mx-auto`}>
                  <i className="ri-star-line text-2xl text-white"></i>
                </div>
                <div className="text-center">
                  <div className="text-white font-extrabold mb-1">{name}</div>
                  <div className="text-white/80 text-sm">{probability}%</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
